import useUserStore from "@/state/user/useUserStore";
import React, { useEffect, useState } from "react";
import { m } from "motion/react";
import { Button, Frame } from "react95";
import { Redo2, Undo2, Plus, Eraser } from "lucide-react";
import { HexColorPicker } from "react-colorful";
import styles from "./HexColorPicker.module.css";

const DrawComponent = ({
  parentVariants,
  undo,
  redo,
  color,
}: {
  parentVariants: any;
  undo: () => void;
  redo: () => void;
  color: string;
}) => {
  const [
    strokeColor,
    setStrokeColor,
    strokeWidth,
    setStrokeWidth,
    isErasing,
    setIsErasing,
  ] = useUserStore((state) => [
    state.strokeColor,
    state.setStrokeColor,
    state.strokeWidth,
    state.setStrokeWidth,
    state.isErasing,
    state.setIsErasing,
  ]);

  const [customColors, setCustomColors] = useState<string[]>(
    Array(14).fill("")
  );
  const [selectedCustomColor, setSelectedCustomColor] =
    useState<string>("#ffffff");
  const [currentColorIndex, setCurrentColorIndex] = useState(0);

  useEffect(() => {
    const newCustomColors = [...customColors];
    newCustomColors[0] = color;
    setCustomColors(newCustomColors);
    setCurrentColorIndex(1);
  }, [color]);

  // Function to handle color change from the color picker
  const handleColorChange = (color: string) => {
    setSelectedCustomColor(color);
  };

  // Updated function to add the selected custom color to the palette
  const addCustomColor = () => {
    const newCustomColors = [...customColors];
    newCustomColors[currentColorIndex] = selectedCustomColor;
    setCustomColors(newCustomColors);
    setCurrentColorIndex((prevIndex) => (prevIndex + 1) % 14);
  };

  return (
    <m.div
      variants={parentVariants}
      initial="initial"
      animate="animate"
      style={{
        background: "#cccccc",
        boxSizing: "border-box",
        borderRadius: "0.25rem",
        overflow: "hidden",
        border: "4px solid #000000",
        boxShadow: "2px 2px 0px 0px #000000",
        // padding: "8px",
        display: "flex",
        flexDirection: "column",
        gap: "6px",
      }}
    >
      <m.div
        style={{
          background: color,
          boxSizing: "border-box",
          padding: "8px",
          borderBottom: "4px solid #000000",
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            position: "relative",
            padding: "0 8px",
          }}
        >
          <p
            style={{
              margin: 0,
              fontFamily: "var(--font-cabin-condensed)",
              fontSize: "20px",
              height: "3rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              color: "#000",
            }}
          >
            Bera Paint
          </p>
        </div>
      </m.div>
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
        }}
      >
        <div
          style={{
            width: "50%",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <p
            style={{
              margin: 0,
              fontFamily: "var(--font-cabin-condensed)",
              padding: "4px 8px",
              boxSizing: "border-box",
            }}
          >
            Stroke Width
          </p>
          <div
            style={{
              width: "100%",
              display: "grid",
              gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
              gap: "4px",
              boxSizing: "border-box",
              padding: "0 8px",
            }}
          >
            {[1, 2, 4, 8, 12, 20, 32].map((width) => (
              <Frame
                key={width}
                variant="field"
                style={{
                  height: "2rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  padding: "0 8px",
                  border: strokeWidth === width ? "2px solid #060083" : "",
                }}
                onClick={() => setStrokeWidth(width)}
              >
                <div
                  style={{
                    background: "#000000",
                    height: `${Math.min(width, 24)}px`,
                    width: "100%",
                  }}
                />
              </Frame>
            ))}
          </div>
          <div
            style={{ width: "100%", display: "flex", flexDirection: "column" }}
          >
            <p
              style={{
                margin: 0,
                fontFamily: "var(--font-cabin-condensed)",
                padding: "4px 8px",
                boxSizing: "border-box",
              }}
            >
              Basic Colors
            </p>
            <div
              style={{
                width: "100%",
                display: "grid",
                gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                gap: "4px",
                boxSizing: "border-box",
                padding: "0 8px",
              }}
            >
              <Frame
                variant="field"
                style={{
                  background: "#000000",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#000000" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#000000")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#464646",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#464646" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#464646")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#787878",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#787878" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#787878")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#b4b4b4",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#b4b4b4" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#b4b4b4")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#dcdcdc",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#dcdcdc" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#dcdcdc")}
              />
              <Frame
                variant="field"
                style={{
                  background: "##ffffff",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#ffffff" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#ffffff")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#990030",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#990030" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#990030")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#9c5a3c",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#9c5a3c" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#9c5a3c")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#ed1c24",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#ed1c24" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#ed1c24")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#ffa3b1",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#ffa3b1" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#ffa3b1")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#ff7e00",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#ff7e00" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#ff7e00")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#e5aa7a",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#e5aa7a" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#e5aa7a")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#ffc20e",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#ffc20e" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#ffc20e")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#f5e49c",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#f5e49c" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#f5e49c")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#fff200",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#fff200" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#fff200")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#fff9bd",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#fff9bd" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#fff9bd")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#a8e61d",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#a8e61d" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#a8e61d")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#d3f9bc",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#d3f9bc" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#d3f9bc")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#22b14c",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#22b14c" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#22b14c")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#9dbb61",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#9dbb61" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#9dbb61")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#00b7ef",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#00b7ef" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#00b7ef")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#99d9ea",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#99d9ea" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#99d9ea")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#4d6df3",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#4d6df3" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#4d6df3")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#709ad1",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#709ad1" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#709ad1")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#2f3699",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#2f3699" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#2f3699")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#546d8e",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#546d8e" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#546d8e")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#6f3198",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#6f3198" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#6f3198")}
              />
              <Frame
                variant="field"
                style={{
                  background: "#b5a5d5",
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  border: strokeColor === "#b5a5d5" ? "3px solid #060083" : "",
                }}
                onClick={() => setStrokeColor("#b5a5d5")}
              />
            </div>
          </div>
          <div
            style={{ width: "100%", display: "flex", flexDirection: "column" }}
          >
            <p
              style={{
                margin: 0,
                fontFamily: "var(--font-cabin-condensed)",
                padding: "4px 8px",
                boxSizing: "border-box",
              }}
            >
              Custom Colors
            </p>
            <div
              style={{
                padding: "0 8px",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "start",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <div
                style={{
                  width: "100%",
                  display: "grid",
                  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                  gap: "4px",
                  boxSizing: "border-box",
                }}
              >
                {customColors.map((color, index) => (
                  <Frame
                    key={index}
                    variant="field"
                    style={{
                      background: color || "transparent",
                      aspectRatio: "1/1",
                      cursor: "pointer",
                      border: strokeColor === color ? "3px solid #060083" : "",
                    }}
                    onClick={() => color && setStrokeColor(color)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            width: "50%",
            display: "flex",
            flexDirection: "column",
            padding: "8px",
            gap: "8px",
          }}
        >
          <Frame
            variant="field"
            style={{
              width: "100%",
            }}
          >
            <HexColorPicker
              color={selectedCustomColor}
              onChange={handleColorChange}
              className={styles.customColorPicker}
              style={{ width: "100%", position: "relative", padding: "2px" }}
            />
          </Frame>
          <Button
            style={{ width: "100%", gap: "4px" }}
            onClick={addCustomColor}
          >
            <Plus
              style={{
                width: "16px",
                height: "16px",
              }}
            />
            add color
          </Button>
        </div>
      </div>

      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          gap: "8px",
          boxSizing: "border-box",
          padding: "8px",
        }}
      >
        <Button style={{ width: "100%", gap: "4px" }} onClick={undo}>
          <Undo2
            style={{
              width: "16px",
              height: "16px",
            }}
          />
          undo
        </Button>
        <Button
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            gap: "8px",
          }}
          onClick={redo}
        >
          <Redo2
            style={{
              width: "16px",
              height: "16px",
            }}
          />
          redo
        </Button>
        <Button
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            gap: "8px",
          }}
          onClick={() => setIsErasing(!isErasing)}
          active={isErasing}
        >
          <Eraser
            style={{
              width: "16px",
              height: "16px",
            }}
          />
          eraser
        </Button>
      </div>
    </m.div>
  );
};

export default DrawComponent;
